/**
 * MetaSearchButton — the magnifier on a ranked row (2026-09-10 owner ask).
 * One tap copies the Pokémon GO search for that Pokémon and says so in a
 * toast, so the trainer can jump to GO and paste. Copy-ready search strings
 * are a Pro feature everywhere else in the app (the Trade string, the Box
 * Cleanup searches), so the same rule holds here: Basic gets the paywall.
 */
import { Search } from "lucide-react";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import { useToast } from "@/hooks/use-toast";
import { copyToClipboard } from "@/lib/copyToClipboard";
import { metaRowSearchString, type MetaSearchInput } from "@/lib/metaSearchString";

export function MetaSearchButton({
  row,
  label,
  testId,
}: {
  row: MetaSearchInput;
  /** The Pokémon's name, for the toast and the accessible label. */
  label: string;
  testId: string;
}) {
  const { tier, openPaywall } = useSession();
  const { toast } = useToast();
  const search = metaRowSearchString(row);
  if (!search) return null;

  const copy = async () => {
    if (!hasProAccess(tier)) {
      openPaywall("meta-search", "Copy-ready GO search strings unlock with Pro");
      return;
    }
    const ok = await copyToClipboard(search);
    toast(
      ok
        ? { title: `${label} search copied`, description: `${search}. Paste it into GO's search bar.` }
        : { title: "Could not copy", variant: "destructive" },
    );
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`Copy the GO search for ${label}`}
      title={search}
      data-testid={testId}
      className="shrink-0 w-7 h-7 rounded-full grid place-items-center"
      style={{
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        color: "var(--text-secondary)",
      }}
    >
      <Search className="w-3.5 h-3.5" aria-hidden />
    </button>
  );
}
